import React from 'react';
import { string, object, array } from 'prop-types';
import classNames from 'classnames';

import { NamedLink } from '../../../components';
import Field from '../../PageBuilder/Field';

import imgLumber from '../../../assets/landing/categories/sheet_goods.jpg';
import imgCement from '../../../assets/landing/categories/cement.jpg';
import imgElectrical from '../../../assets/landing/categories/electrical.jpg';
import imgPlumbing from '../../../assets/landing/categories/plumbing.jpg';
import imgRoofing from '../../../assets/landing/categories/roofing.jpg';
import imgTools from '../../../assets/landing/categories/tools.jpg';
import imgFlooring from '../../../assets/landing/categories/flooring.jpg';
import imgDoors from '../../../assets/landing/categories/doors.jpg';
import imgHvac from '../../../assets/landing/categories/hvac.jpg';
import imgPaint from '../../../assets/landing/categories/paint.jpg';
import imgFixtures from '../../../assets/landing/categories/fixtures.jpg';
import imgLandscaping from '../../../assets/landing/categories/landscaping.jpg';

import css from './CategoriesSection.module.css';

// Fallback categories with images when Console blocks aren't configured
const FALLBACK_CATEGORIES = [
  { name: 'Lumber', image: imgLumber },
  { name: 'Cement & Concrete', image: imgCement },
  { name: 'Electrical & Wiring', image: imgElectrical },
  { name: 'Plumbing & Drainage', image: imgPlumbing },
  { name: 'Roofing & Siding', image: imgRoofing },
  { name: 'Tools & Equipment', image: imgTools },
  { name: 'Flooring', image: imgFlooring },
  { name: 'Doors & Hardware', image: imgDoors },
  { name: 'HVAC', image: imgHvac },
  { name: 'Paint & Coatings', image: imgPaint },
  { name: 'Fixtures & Finishes', image: imgFixtures },
  { name: 'Landscaping & Exterior', image: imgLandscaping },
];

/**
 * Custom Categories Grid Section for ConstructionYardSale.com
 * Renders Console blocks as category cards in a responsive grid.
 * Each block maps to a category with image overlay and name.
 * Matches the Replit design: image cards with gradient overlay + category name.
 *
 * Console setup: Create a "columns" or "features" section with blocks,
 * each block having a title (category name) and optional media (image).
 */
const CategoriesSection = props => {
  const {
    sectionId,
    className,
    title,
    description,
    callToAction,
    blocks,
    options,
    ...rest
  } = props;

  const hasBlocks = blocks && blocks.length > 0;
  const hasTitle = title?.content;
  const hasInternalCta =
    callToAction?.fieldType === 'internalButtonLink' &&
    !!callToAction?.href &&
    !!callToAction?.content;
  const hasExternalCta =
    callToAction?.fieldType === 'externalButtonLink' &&
    !!callToAction?.href &&
    !!callToAction?.content;

  return (
    <section id={sectionId} className={classNames(css.root, className)}>
      <div className={css.container}>
        <div className={css.header}>
          {hasTitle ? (
            <Field data={title} className={css.sectionTitle} />
          ) : (
            <h2 className={css.sectionTitle}>Shop by Category</h2>
          )}
          {description?.content && (
            <Field data={description} className={css.sectionDescription} />
          )}
        </div>

        {hasBlocks ? (
          <div className={css.grid}>
            {blocks.map((block, index) => {
              const blockTitle = block?.title?.content || `Category ${index + 1}`;
              const blockMedia = block?.media;
              const hasImage = blockMedia?.image?.id;

              return (
                <NamedLink
                  key={block.blockId || index}
                  name="SearchPage"
                  to={{ search: `?pub_categoryLevel1=${encodeURIComponent(blockTitle)}` }}
                  className={css.categoryCard}
                >
                  {hasImage && (
                    <div className={css.imageWrapper}>
                      <Field data={blockMedia} className={css.image} />
                    </div>
                  )}
                  <div className={css.overlay} />
                  <span className={css.categoryName}>{blockTitle}</span>
                </NamedLink>
              );
            })}
          </div>
        ) : (
          <div className={css.grid}>
            {FALLBACK_CATEGORIES.map((cat, index) => (
              <NamedLink
                key={index}
                name="SearchPage"
                to={{ search: `?pub_categoryLevel1=${encodeURIComponent(cat.name)}` }}
                className={css.categoryCard}
              >
                <div className={css.imageWrapper}>
                  <img src={cat.image} alt={cat.name} className={css.categoryImage} />
                </div>
                <div className={css.overlay} />
                <span className={css.categoryName}>{cat.name}</span>
              </NamedLink>
            ))}
          </div>
        )}

        {hasInternalCta ? (
          <div className={css.viewAllRow}>
            <a href={callToAction.href} className={css.viewAllButton}>
              {callToAction.content}
            </a>
          </div>
        ) : hasExternalCta ? (
          <div className={css.viewAllRow}>
            <a
              href={callToAction.href}
              target="_blank"
              rel="noopener noreferrer"
              className={css.viewAllButton}
            >
              {callToAction.content}
            </a>
          </div>
        ) : null}
      </div>
    </section>
  );
};

CategoriesSection.propTypes = {
  sectionId: string.isRequired,
  className: string,
  title: object,
  description: object,
  callToAction: object,
  blocks: array,
  options: object,
};

export default CategoriesSection;
